import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Textarea } from '../../lib/shadcn/textarea'
import { Label } from '../../lib/shadcn/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import type { IngredientCategory } from './IngredientCategoriesDialog'
import { useUpsertIngredient } from '../../hooks/backend/recipes'
import { useGetUnits } from '../../hooks/backend/settingsPage'
import { usePublishLabelProducts } from '../../hooks/backend/inventoryPage'
import { publishLabels } from '../../utils/publishLabels'
import { toast } from '../../lib/shadcn/sonner'
import UnitSelect from './UnitSelect'
import type { Unit } from '../../utils/units'
import { autoDetectAllergens, detectAllergens, formatAllergens } from '../../utils/allergens'
import { formatUnitCost } from '../../utils/format'
import { HelpText } from '../../components/HelpText'

export interface IngredientWithComponents {
  id: string
  name: string
  /** Printed on ingredient lists instead of `name` (e.g. POPCORN for Popcorn Kernels). */
  list_name?: string | null
  cost: number | null
  cost_unit: string | null
  /** Pack quantity the price was entered for (e.g. 16 for "$28.80 for 16 oz"); null = 1. */
  cost_qty: number | null
  sub_ingredients: string | null
  allergens: string | null
  density_weight: number | null
  density_weight_unit: string | null
  density_volume: number | null
  density_volume_unit: string | null
  /** Category id; null = Uncategorized. */
  category_id?: string | null
  category_name?: string | null
}

const NO_CATEGORY = '__none'

interface IngredientFormDialogProps {
  ingredient: IngredientWithComponents | null
  /** Prefill from `ingredient` but save as a new ingredient. */
  duplicate?: boolean
  /** Names already in use, so a new ingredient can't overwrite one (the insert upserts by name). */
  existingNames?: string[]
  /** Managed ingredient categories to choose from. */
  categories?: IngredientCategory[]
  /** Category a new ingredient starts in (e.g. the one the table is filtered to). */
  defaultCategoryId?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}

export default function IngredientFormDialog({
  ingredient,
  duplicate = false,
  existingNames = [],
  categories = [],
  defaultCategoryId = null,
  open,
  onOpenChange,
  onSaved,
}: IngredientFormDialogProps) {
  const upsertIngredient = useUpsertIngredient()
  const publishLabelProducts = usePublishLabelProducts()
  const getUnits = useGetUnits()
  const units = (getUnits.data as Unit[] | undefined) ?? []
  const [name, setName] = useState('')
  const [listName, setListName] = useState('')
  const [cost, setCost] = useState('')
  const [costQty, setCostQty] = useState('')
  const [costUnit, setCostUnit] = useState('')
  const [subIngredients, setSubIngredients] = useState('')
  const [allergens, setAllergens] = useState('')
  const [densWeight, setDensWeight] = useState('')
  const [densWeightUnit, setDensWeightUnit] = useState('')
  const [densVolume, setDensVolume] = useState('')
  const [densVolumeUnit, setDensVolumeUnit] = useState('')
  const [categoryId, setCategoryId] = useState(NO_CATEGORY)
  const isNew = !ingredient || duplicate

  useEffect(() => {
    if (open) {
      setName(ingredient ? (duplicate ? `${ingredient.name} (copy)` : ingredient.name) : '')
      setListName((ingredient?.list_name ?? '').toUpperCase())
      // cost is stored per one unit; show the price for the pack size it was entered for.
      const qty = ingredient?.cost_qty && ingredient.cost_qty > 0 ? ingredient.cost_qty : 1
      setCostQty(String(qty))
      setCost(ingredient?.cost != null ? String(Number((ingredient.cost * qty).toFixed(4))) : '')
      setCostUnit(ingredient?.cost_unit ?? '')
      setSubIngredients((ingredient?.sub_ingredients ?? '').toUpperCase())
      setAllergens((ingredient?.allergens ?? '').toUpperCase())
      setDensWeight(ingredient?.density_weight != null ? String(ingredient.density_weight) : '')
      setDensWeightUnit(ingredient?.density_weight_unit ?? '')
      setDensVolume(ingredient?.density_volume != null ? String(ingredient.density_volume) : '')
      setDensVolumeUnit(ingredient?.density_volume_unit ?? '')
      setCategoryId((ingredient ? ingredient.category_id : defaultCategoryId) || NO_CATEGORY)
      void getUnits.trigger({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ingredient, duplicate, defaultCategoryId])

  const detectedAllergens = formatAllergens(
    autoDetectAllergens({ name, sub_ingredients: subIngredients }),
  )
  const manualAllergens = formatAllergens(detectAllergens({ allergens }))

  const costNum = cost.trim() === '' ? null : Number(cost)
  const qtyNum = costQty.trim() === '' ? 1 : Number(costQty)
  const unitCost =
    costNum != null && !Number.isNaN(costNum) && qtyNum > 0 ? costNum / qtyNum : null

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      toast.error('Ingredient name is required')
      return
    }
    if (isNew && existingNames.some(n => n.trim().toLowerCase() === trimmed.toLowerCase())) {
      toast.error(`An ingredient named “${trimmed}” already exists`)
      return
    }
    const parsedCost = cost.trim() === '' ? null : Number(cost)
    if (parsedCost != null && (Number.isNaN(parsedCost) || parsedCost < 0)) {
      toast.error('Cost must be a valid non-negative number')
      return
    }
    const parsedQty = costQty.trim() === '' ? 1 : Number(costQty)
    if (Number.isNaN(parsedQty) || parsedQty <= 0) {
      toast.error('Quantity must be a number greater than 0')
      return
    }
    try {
      const res = (await upsertIngredient.trigger({
        ...(ingredient?.id && !duplicate ? { id: ingredient.id } : {}),
        name: trimmed,
        list_name: listName.trim().toUpperCase() || null,
        cost: parsedCost == null ? null : parsedCost / parsedQty,
        cost_qty: parsedQty === 1 ? null : parsedQty,
        cost_unit: costUnit.trim() || null,
        sub_ingredients: subIngredients.trim().toUpperCase() || null,
        allergens: allergens.trim().toUpperCase() || null,
        density_weight: densWeight.trim() === '' ? null : Number(densWeight),
        density_weight_unit: densWeightUnit.trim() || null,
        density_volume: densVolume.trim() === '' ? null : Number(densVolume),
        density_volume_unit: densVolumeUnit.trim() || null,
        category_id: categoryId === NO_CATEGORY ? null : categoryId,
      })) as { productsUpdated?: number; publishLabels?: string[] } | undefined
      const n = res?.productsUpdated ?? 0
      toast.success(
        (isNew ? 'Ingredient created' : 'Ingredient updated') +
          (n > 0 ? ` — ingredient list and allergens updated on ${n} product${n === 1 ? '' : 's'}` : ''),
      )
      onSaved()
      onOpenChange(false)
      void publishLabels(res?.publishLabels ?? [], params => publishLabelProducts.trigger(params))
    } catch (error) {
      toast.error(
        `Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {duplicate ? 'Duplicate Ingredient' : ingredient ? 'Edit Ingredient' : 'New Ingredient'}
          </DialogTitle>
          <DialogDescription>
            {duplicate
              ? `A copy of ${ingredient?.name ?? 'this ingredient'}. Give it a new name and adjust anything before saving.`
              : 'Set what the ingredient costs and, for compound ingredients, the sub-ingredients that make it up.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 flex-1 overflow-y-auto min-h-0">
          <div className="space-y-1.5">
            <Label htmlFor="ingredient-name" className="text-sm font-medium text-muted-foreground">
              Name
            </Label>
            <Input
              id="ingredient-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Butter"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ingredient-list-name" className="text-sm font-medium text-muted-foreground">
              Name on ingredient list (optional)
            </Label>
            <Input
              id="ingredient-list-name"
              value={listName}
              onChange={e => setListName(e.target.value.toUpperCase())}
              placeholder={name.trim() ? name.trim().toUpperCase() : 'e.g. POPCORN'}
            />
            <p className="text-xs text-muted-foreground"><HelpText>
              What product ingredient lists call this ingredient, e.g. POPCORN for
              Popcorn Kernels. Leave blank to use the name above.
            </HelpText></p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ingredient-category" className="text-sm font-medium text-muted-foreground">
              Category
            </Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger id="ingredient-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CATEGORY}>Uncategorized</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
                {/* Keep a category that was deleted elsewhere selectable until the list reloads. */}
                {categoryId !== NO_CATEGORY && !categories.some(c => c.id === categoryId) ? (
                  <SelectItem value={categoryId}>{ingredient?.category_name ?? 'Unknown category'}</SelectItem>
                ) : null}
              </SelectContent>
            </Select>
            {categories.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No categories yet. Add them with the Categories button above the ingredients table.
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-[1fr_auto_5rem_1fr] items-end gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="ingredient-cost" className="text-sm font-medium text-muted-foreground">
                Cost
              </Label>
              <Input
                id="ingredient-cost"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={cost}
                onChange={e => setCost(e.target.value)}
                placeholder="e.g. 28.80"
              />
            </div>
            <span className="pb-2 text-sm text-muted-foreground">for</span>
            <div className="space-y-1.5">
              <Label htmlFor="ingredient-qty" className="text-sm font-medium text-muted-foreground">
                Qty
              </Label>
              <Input
                id="ingredient-qty"
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                value={costQty}
                onChange={e => setCostQty(e.target.value)}
                placeholder="1"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ingredient-unit" className="text-sm font-medium text-muted-foreground">
                Unit
              </Label>
              <UnitSelect
                id="ingredient-unit"
                value={costUnit}
                onChange={setCostUnit}
                units={units}
                placeholder="Select unit"
              />
            </div>
          </div>
          {unitCost != null && qtyNum !== 1 ? (
            <p className="-mt-2 text-xs">
              <span className="text-muted-foreground">Works out to </span>
              <span className="font-medium tabular-nums">
                {formatUnitCost(unitCost)}
                {costUnit ? ` per ${costUnit}` : ' per unit'}
              </span>
            </p>
          ) : null}
          <p className="-mt-2 text-xs text-muted-foreground"><HelpText>
            Enter the price for any amount (e.g. $28.80 for 16 oz). Recipes can use any
            compatible unit (weight↔weight, volume↔volume) and the cost is converted
            automatically.
          </HelpText></p>

          <div className="space-y-1.5">
            <Label htmlFor="ingredient-sub" className="text-sm font-medium text-muted-foreground">
              Sub-ingredients
            </Label>
            <Textarea
              id="ingredient-sub"
              value={subIngredients}
              onChange={e => setSubIngredients(e.target.value.toUpperCase())}
              placeholder="e.g. CREAM, SALT"
              rows={2}
            />
            <p className="text-xs text-muted-foreground"><HelpText>
              Open text, in order of predominance. Shown in parentheses after this
              ingredient in a product's FDA ingredient list. Leave blank for a base
              ingredient.
            </HelpText></p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ingredient-allergens" className="text-sm font-medium text-muted-foreground">
              Allergens
            </Label>
            <Input
              id="ingredient-allergens"
              value={allergens}
              onChange={e => setAllergens(e.target.value.toUpperCase())}
              placeholder={detectedAllergens || 'NONE DETECTED'}
            />
            <p className="text-xs text-muted-foreground"><HelpText>
              Leave blank to use what's detected from the name and sub-ingredients
              ({detectedAllergens || 'none'}). Enter them yourself to override, e.g.
              MILK, SOY — or NONE if this ingredient has no allergens.
            </HelpText></p>
            {allergens.trim() !== '' ? (
              <p className="text-xs">
                <span className="text-muted-foreground">Counts as: </span>
                <span className="font-medium">{manualAllergens || 'No major allergens'}</span>
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-muted-foreground">
              Density (optional)
            </Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                value={densWeight}
                onChange={e => setDensWeight(e.target.value)}
                placeholder="e.g. 2"
                className="w-20"
                aria-label="Density weight amount"
              />
              <UnitSelect
                value={densWeightUnit}
                onChange={setDensWeightUnit}
                units={units}
                filterType="weight"
                placeholder="wt unit"
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">per</span>
              <Input
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                value={densVolume}
                onChange={e => setDensVolume(e.target.value)}
                placeholder="e.g. 1"
                className="w-20"
                aria-label="Density volume amount"
              />
              <UnitSelect
                value={densVolumeUnit}
                onChange={setDensVolumeUnit}
                units={units}
                filterType="volume"
                placeholder="vol unit"
                className="w-24"
              />
            </div>
            <p className="text-xs text-muted-foreground"><HelpText>
              How much a given volume weighs (e.g. 2 oz per 1 gallon). Lets recipes
              measured by volume be costed against a weight-based price (and vice versa).
            </HelpText></p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={upsertIngredient.loading}>
            {upsertIngredient.loading ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
